<template>
    <div class="applicants-page">
      <button @click="goBack" type="button" class="back-button">이전으로</button>
      <h2>청약 목록</h2>
      <table class="subscription-table">
        <thead>
          <tr>
            <th>제목</th>
            <th>기간</th>
            <th>신청자 보기</th>
          </tr>
        </thead>
        <tbody>
            <tr v-for="item in paginatedSubscriptions" :key="item.id">
                <td>{{ item.title }}</td>
                <td>{{ formatDate(item.startTs) }} ~ {{ formatDate(item.endTs) }}</td>
                <td><button @click="fetchApplicants(item.id)">보기</button></td>
            </tr>
        </tbody>
      </table>

    <div class="pagination" v-if="totalSubscriptionPages > 1">
        <button :disabled="subscriptionPage === 1" @click="subscriptionPage--">이전</button>
        <span>{{ subscriptionPage }} / {{ totalSubscriptionPages }}</span>
        <button :disabled="subscriptionPage === totalSubscriptionPages" @click="subscriptionPage++">다음</button>
    </div>
  
      <div v-if="applicants.length > 0" class="applicant-table">
        <div class="applicant-table-header">
            <h3>신청자 목록</h3>
            <div class="header-actions">
              <button @click="saveSelectionStatus" class="save-button" :disabled="!hasChanges">변경사항 저장</button>
              <button @click="downloadApplicantsExcel">엑셀 다운로드</button>
            </div>
        </div>

        <!-- 검색 기능 추가 -->
        <div class="search-container">
          <select v-model="searchType" class="search-select">
            <option value="name">이름</option>
            <option value="phoneNumber">전화번호</option>
          </select>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="searchType === 'name' ? '이름으로 검색' : '전화번호로 검색'" 
            class="search-input"
          />
          <button @click="handleSearch" class="search-button">검색</button>
          <button v-if="isSearchActive" @click="clearSearch" class="clear-button">초기화</button>
        </div>

        <table>
          <thead>
            <tr>
              <th>이름</th>
              <th>전화번호</th>
              <th>신청유형</th>
              <th>신청일</th>
              <th>당첨상태</th> <!-- 당첨 상태 칼럼 -->
              <th>예비순번</th> <!-- 예비 순번 칼럼 -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a) in paginatedApplicants" :key="a.id">
                <td>{{ a.name }}</td>
                <td>{{ a.phoneNumber }}</td>
                <td>{{ mapSupplyTypeLabel(a.subscriptionType) }}</td>
                <td>{{ formatDate(a.createdAt) }}</td>
                <td>
                  <!-- 당첨 상태 선택 드롭다운 -->
                  <select 
                    v-model="a.displayStatus" 
                    class="selection-status"
                    :class="getStatusClass(a.displayStatus)"
                    @change="handleStatusChange(a.id, $event.target.value)"
                  >
                    <option value="당첨">당첨</option>
                    <option value="예비">예비</option>
                    <option value="미당첨">미당첨</option>
                  </select>
                </td>
                <td>
                  <!-- 예비 순번 선택 (예비일 때만 표시) -->
                  <div v-if="a.displayStatus === '예비'" class="reserve-number-container">
                    <input 
                      type="number" 
                      v-model="a.reserveNumber" 
                      class="reserve-number-input" 
                      min="1"
                      @change="handleReserveNumberChange(a.id, $event.target.value)"
                      placeholder="순번"
                    />
                  </div>
                  <div v-else class="reserve-number-placeholder">
                    {{ a.displayStatus === '당첨' ? '-' : '' }}
                  </div>
                </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination" v-if="totalPages > 1">
            <button :disabled="currentPage === 1" @click="currentPage--">이전</button>
            <span>{{ currentPage }} / {{ totalPages }}</span>
            <button :disabled="currentPage === totalPages" @click="currentPage++">다음</button>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router';
import axios from '@/utils/axios';
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const router = useRouter()
const subscriptions = ref([])
const applicants = ref([])
const filteredApplicants = ref([])
const searchQuery = ref('')
const searchType = ref('name') // 기본값은 이름으로 검색
const isSearchActive = ref(false)
const currentSubscriptionId = ref(null)
const hasChanges = ref(false)
const changedStatuses = ref({}) // 변경된 상태를 추적하는 객체

const currentPage = ref(1)
const itemsPerPage = 10
const subscriptionPage = ref(1)
const subscriptionsPerPage = 5

const goBack = (() => {
  router.push('/admin')
})

const paginatedSubscriptions = computed(() => {
  const start = (subscriptionPage.value - 1) * subscriptionsPerPage
  return subscriptions.value.slice(start, start + subscriptionsPerPage)
})

const totalSubscriptionPages = computed(() =>
  Math.ceil(subscriptions.value.length / subscriptionsPerPage)
)

const paginatedApplicants = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const applicantsToUse = isSearchActive.value ? filteredApplicants.value : applicants.value
  return applicantsToUse.slice(start, start + itemsPerPage)
})

const totalPages = computed(() => {
  const applicantsToUse = isSearchActive.value ? filteredApplicants.value : applicants.value
  return Math.ceil(applicantsToUse.length / itemsPerPage)
})

// 서버에서 받은 winningState 값을 화면 표시용 상태와 예비 순번으로 분리
const parseWinningState = (winningState) => {
  if (!winningState) return { displayStatus: '미당첨', reserveNumber: null }
  
  // "예비" 다음에 숫자가 있는지 확인
  if (winningState.startsWith('예비') && winningState.length > 2) {
    const reserveNumber = parseInt(winningState.substring(2), 10)
    if (!isNaN(reserveNumber)) {
      return { displayStatus: '예비', reserveNumber }
    }
  }
  
  // "당첨"인 경우
  if (winningState === '당첨') {
    return { displayStatus: '당첨', reserveNumber: null }
  }
  
  // 그 외의 모든 경우는 "미당첨"으로 처리
  return { displayStatus: '미당첨', reserveNumber: null }
}

// 화면 표시용 상태와 예비 순번을 서버 저장용 상태값으로 결합
const combineWinningState = (displayStatus, reserveNumber) => {
  if (displayStatus === '예비' && reserveNumber) {
    return `예비${reserveNumber}`
  }
  return displayStatus
}

const fetchSubscriptions = async () => {
  const res = await axios.get("/bx201/subscription")
  subscriptions.value = res.data
}

const fetchApplicants = async (subscriptionId) => {
  currentSubscriptionId.value = subscriptionId
  const res = await axios.get(`/bx201/subscriptionApplicants/by-subscription/${subscriptionId}`)
  
  // 응답 데이터가 배열이 아니면 빈 배열로 처리
  const applicantData = Array.isArray(res.data) ? res.data : []

  applicants.value = applicantData.map(applicant => {
    // 서버에서 받은 winningState 값 파싱
    const { displayStatus, reserveNumber } = parseWinningState(applicant.winningState || '')
    return {
      ...applicant,
      originalWinningState: applicant.winningState || '',
      displayStatus,
      reserveNumber
    }
  })

  filteredApplicants.value = [] // 검색 결과 초기화
  isSearchActive.value = false
  searchQuery.value = ''
  currentPage.value = 1 // 페이지 초기화
  hasChanges.value = false
  changedStatuses.value = {}
}

const handleSearch = () => {
  if (!searchQuery.value.trim()) {
    // 검색어가 비어있으면 전체 목록 표시
    clearSearch()
    return
  }

  const query = searchQuery.value.toLowerCase()
  
  filteredApplicants.value = applicants.value.filter(applicant => {
    if (searchType.value === 'name') {
      return applicant.name.toLowerCase().includes(query)
    } else {
      return applicant.phoneNumber.includes(query)
    }
  })
  
  isSearchActive.value = true
  currentPage.value = 1 // 검색 후 첫 페이지로 이동
}

const clearSearch = () => {
  searchQuery.value = ''
  isSearchActive.value = false
  currentPage.value = 1 // 초기화 후 첫 페이지로 이동
}

// 당첨 상태 변경 처리
const handleStatusChange = (applicantId, newStatus) => {
  // 실제 applicants 배열의 해당 항목 찾기
  const applicant = applicants.value.find(a => a.id === applicantId)
  if (!applicant) return
  
  // 화면 표시용 상태 업데이트
  applicant.displayStatus = newStatus
  
  // 예비가 아닌 상태로 변경되면 예비 순번 초기화
  if (newStatus !== '예비') {
    applicant.reserveNumber = null
  }
  
  // 변경된 상태 추적 (서버에 보낼 형태로 결합)
  const combinedState = combineWinningState(newStatus, applicant.reserveNumber)
  if (combinedState !== applicant.originalWinningState) {
    changedStatuses.value[applicantId] = combinedState
    hasChanges.value = true
  } else {
    // 원래 상태와 같아지면 변경 목록에서 제거
    if (changedStatuses.value[applicantId]) {
      delete changedStatuses.value[applicantId]
      hasChanges.value = Object.keys(changedStatuses.value).length > 0
    }
  }
  
  // 검색 결과에도 반영
  if (isSearchActive.value) {
    const filteredApplicant = filteredApplicants.value.find(a => a.id === applicantId)
    if (filteredApplicant) {
      filteredApplicant.displayStatus = newStatus
      if (newStatus !== '예비') {
        filteredApplicant.reserveNumber = null
      }
    }
  }
}

// 예비 순번 변경 처리
const handleReserveNumberChange = (applicantId, newNumber) => {
  // 숫자로 변환
  const numberValue = parseInt(newNumber, 10)
  
  // 유효한 숫자인지 확인
  if (isNaN(numberValue) || numberValue < 1) {
    return
  }
  
  // 실제 applicants 배열의 해당 항목 업데이트
  const applicant = applicants.value.find(a => a.id === applicantId)
  if (!applicant || applicant.displayStatus !== '예비') return
  
  // 예비 순번 업데이트
  applicant.reserveNumber = numberValue
  
  // 변경된 상태 추적 (서버에 보낼 형태로 결합)
  const combinedState = combineWinningState('예비', numberValue)
  if (combinedState !== applicant.originalWinningState) {
    changedStatuses.value[applicantId] = combinedState
    hasChanges.value = true
  } else {
    // 원래 상태와 같아지면 변경 목록에서 제거
    if (changedStatuses.value[applicantId]) {
      delete changedStatuses.value[applicantId]
      hasChanges.value = Object.keys(changedStatuses.value).length > 0
    }
  }
  
  // 검색 결과에도 반영
  if (isSearchActive.value) {
    const filteredApplicant = filteredApplicants.value.find(a => a.id === applicantId)
    if (filteredApplicant) {
      filteredApplicant.reserveNumber = numberValue
    }
  }
}

// 당첨 상태에 따른 클래스 반환
const getStatusClass = (status) => {
  switch (status) {
    case '당첨': return 'status-selected'
    case '예비': return 'status-reserve'
    case '미당첨': return 'status-failed'
    default: return 'status-failed' // 기본값도 미당첨 스타일 적용
  }
}
// ✅ 변경사항 저장 함수 - 예비 순번 중복 체크 추가
const saveSelectionStatus = async () => {
  try {
    if (!currentSubscriptionId.value || Object.keys(changedStatuses.value).length === 0) {
      return;
    }

    // ✅ 예비 순번 중복 검사
    const reserveNumbers = applicants.value
      .filter(a => a.displayStatus === '예비')
      .map(a => a.reserveNumber)
      .filter(num => num !== null);

    const uniqueNumbers = new Set(reserveNumbers);
    if (reserveNumbers.length !== uniqueNumbers.size) {
      alert("예비 순번이 중복되었습니다. 중복된 순번을 수정하세요.");
      return;
    }

    // ✅ API 호출하여 변경사항 저장
    await axios.put('/bx201/subscriptionApplicants/update-winning-state', {
      subscriptionId: currentSubscriptionId.value,
      statusUpdates: Object.entries(changedStatuses.value).map(([id, state]) => ({
        applicantId: id,
        winningState: state // 이미 "예비1"과 같은 형태로 결합된 상태
      }))
    });

    // 저장 성공 후 원래 상태값 업데이트
    Object.entries(changedStatuses.value).forEach(([id, state]) => {
      const applicant = applicants.value.find(a => a.id === id);
      if (applicant) {
        applicant.originalWinningState = state;
      }
    });

    // 저장 성공 처리
    alert('당첨 상태가 성공적으로 저장되었습니다.');
    hasChanges.value = false;
    changedStatuses.value = {};
  } catch (error) {
    alert('당첨 상태 저장 중 오류가 발생했습니다.');
  }
};


const formatDate = (d) => {
  const date = new Date(d)
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const mapSupplyTypeLabel = (type) => {
  switch (type) {
    case 'GENERAL-15': return '15평 청년 일반공급';
    case 'SPECIAL-15': return '15평 청년 특별공급';
    case 'SPECIAL-30': return '30평 청년&신혼부부 일반공급';
    default: return type;
  }
}

const downloadApplicantsExcel = () => {
  if (applicants.value.length === 0) {
    alert('다운로드할 신청자 목록이 없습니다.')
    return
  }

  // 검색 결과가 있으면 검색 결과만 다운로드, 아니면 전체 목록 다운로드
  const dataToDownload = isSearchActive.value ? filteredApplicants.value : applicants.value

  // 엑셀에 넣을 데이터 배열 구성
  const worksheetData = dataToDownload.map(a => {
    const data = {
      이름: a.name,
      연락처: a.phoneNumber,
      신청유형: mapSupplyTypeLabel(a.subscriptionType),
      신청일: formatDate(a.createdAt),
      당첨상태: a.displayStatus || '미당첨'
    }
    
    // 예비인 경우 예비 순번 추가
    if (a.displayStatus === '예비') {
      data['예비순번'] = a.reserveNumber || '미지정'
    } else {
      data['예비순번'] = '-'
    }

        // 특별공급(SPECIAL-15)이면 추가 필드 넣기
    if (a.subscriptionType === 'SPECIAL-15') {
      data['소득기준'] = a.income || '';
      data['지역기준'] = a.regional || '';
      data['자동차 소지 유무'] = a.car === true ? '유' : (a.car === false ? '무' : '');
      data['자산기준'] = a.assetBasis === true
        ? 'O'
        : '';
    }
    
    return data
  })

  const worksheet = XLSX.utils.json_to_sheet(worksheetData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, '신청자목록')

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
  saveAs(blob, '청약_신청자목록.xlsx')
}

onMounted(fetchSubscriptions)
</script>

<style scoped>
.applicants-page {
  max-width: 1200px;
  margin: 40px auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  font-family: 'Noto Sans KR', sans-serif;
}

/* 공통 테이블 스타일 */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-size: 0.95rem;
}

th, td {
  padding: 12px;
  border: 1px solid #ddd;
  text-align: center;
}

thead {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #333;
}

h2, h3 {
  color: #222;
  margin-top: 0;
}

/* 보기 버튼 */
button {
  background-color: #007bff;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

button:hover {
  background-color: #0056b3;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* 검색 컨테이너 스타일 */
.search-container {
  display: flex;
  gap: 10px;
  margin: 20px 0;
  align-items: center;
}

.search-select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  font-size: 0.9rem;
  min-width: 100px;
}

.search-input {
  flex: 1;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
}

.search-button {
  background-color: #007bff;
  color: white;
}

.clear-button {
  background-color: #6c757d;
  color: white;
}

/* 당첨 상태 선택 스타일 */
.selection-status {
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  width: 100px;
}

/* 예비 순번 입력 스타일 */
.reserve-number-container {
  display: flex;
  justify-content: center;
}

.reserve-number-input {
  width: 70px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  text-align: center;
}

.reserve-number-placeholder {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 당첨 상태별 색상 */
.status-selected {
  background-color: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}

.status-reserve {
  background-color: #fff3cd;
  color: #856404;
  border-color: #ffeeba;
}

.status-failed {
  background-color: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}

/* 저장 버튼 */
.save-button {
  background-color: #28a745;
  margin-right: 10px;
}

.save-button:hover {
  background-color: #218838;
}

/* 신청자 테이블 별도 마진 */
.applicant-table {
  margin-top: 40px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
}

.pagination button {
  padding: 6px 12px;
  border: 1px solid #ccc;
  background-color: white;
  cursor: pointer;
}

.pagination button:disabled {
  color: #aaa;
  cursor: not-allowed;
}

.applicant-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 반응형 (모바일 대응 간단히) */
@media (max-width: 768px) {
  th, td {
    font-size: 0.85rem;
    padding: 8px;
  }

  button {
    padding: 6px 10px;
    font-size: 0.8rem;
  }
  
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-select, .search-input, .search-button, .clear-button {
    width: 100%;
    margin-bottom: 8px;
  }
  
  .selection-status {
    width: 80px;
    padding: 4px 6px;
  }
  
  .reserve-number-input {
    width: 50px;
    padding: 4px 6px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 5px;
  }
}
</style>